<script lang="ts">
  import { link, location, push, querystring } from "svelte-spa-router";
  import { selectedOrgStore } from "$lib/stores/orgs";
  import { sidebar } from "$lib/stores/sidebar.svelte";
  import { derived as storeDerived } from "svelte/store";
  import SortableColumnHeader from "./SortableColumnHeader.svelte";
  import { toTitleCase } from "$lib/utils/text";
  import SeverityBadge from "./SeverityBadge.svelte";
  import FindingFlyover from "./FindingFlyover.svelte";
  import type { SecurityFinding } from "$lib/stores/orgSecurityFindings";
  import ImpactBadge from "./ImpactBadge.svelte";
  import ResourceTypeLogo from "./ResourceTypeLogo.svelte";
  import { find, map } from "lodash";

  enum SortableField {
    RESOURCE_NAME = "resourceName",
    SECURITY_FINDING_KIND = "securityFindingKind",
    SEVERITY = "severity",
    CONFIDENCE = "confidence",
    IMPACT = "impact",
    CREATED_DATE = "findingCreatedDate",
    STATUS = "status",
  }

  let { findings, onSort, sortField, sortDirection, providers } = $props<{
    findings: SecurityFinding[];
    providers: string[];
    onSort: (column: string, direction: "asc" | "desc") => void;
    sortField?: string;
    sortDirection?: "asc" | "desc";
  }>();
  let selectedFinding = $state<SecurityFinding | null>(null);

  storeDerived([querystring], ([qs]) => ({ qs })).subscribe(({ qs }) => {
    const findingId = new URLSearchParams(qs ?? "").get("findingId");
    // An id the page does not list (a hidden kind, or unknown) is "not found": no flyover.
    selectedFinding = findingId
      ? (findings.find((finding: SecurityFinding) => finding.id === findingId) ?? null)
      : null;
    if (selectedFinding) {
      sidebar.snippet = FindingFlyoverSnippet;
    } else if (sidebar.snippet === FindingFlyoverSnippet) {
      // Only close our own flyover; another page may own the sidebar.
      sidebar.snippet = null;
    }
  });

  $effect(() => {
    // Remove URL parameter when drawer is closed
    if (!selectedFinding) {
      console.log(`[FindingList] Sidebar closed, cleaning up`);
      selectedFinding = null;

      const sp = new URLSearchParams($querystring);
      if (sp.has("findingId")) {
        sp.delete("findingId");
        let url = $location;
        if (sp.size > 0) url += `?${sp.toString()}`;
        console.log(`[FindingList] Updating URL:`, { url });
        push(url);
      }
    }
  });

  const getDetailViewUrl = (finding: SecurityFinding) => {
    const sp = new URLSearchParams($querystring);
    sp.set("findingId", finding.id);
    const currentUrl = $location;
    return `${currentUrl}?${sp.toString()}`;
  };
</script>

{#snippet FindingFlyoverSnippet()}
  <FindingFlyover finding={selectedFinding} />
{/snippet}

<div class="overflow-x-auto">
  <div class="bg-gray-50">
    <div class="grid grid-cols-6 w-full text-left border-b border-gray-400">
      <div class="px-4 py-3 col-span-2 flex items-center">
        <div class="text-xs font-medium text-gray-500 title-case">Resource(s)</div>
      </div>
      <div class="px-4 py-3">
        <SortableColumnHeader column={SortableField.SEVERITY} label="Severity" {sortField} {sortDirection} {onSort} />
      </div>
      <div class="px-4 py-3">
        <SortableColumnHeader column={SortableField.IMPACT} label="Impact" {sortField} {sortDirection} {onSort} />
      </div>
      <div class="px-4 py-3">
        <SortableColumnHeader
          column={SortableField.CONFIDENCE}
          label="Confidence"
          {sortField}
          {sortDirection}
          {onSort}
        />
      </div>

      <div class="px-4 py-3">
        <SortableColumnHeader column={SortableField.STATUS} label="Status" {sortField} {sortDirection} {onSort} />
      </div>
    </div>

    <div class="bg-white divide-y divide-gray-100">
      {#each findings as finding}
        <a href={getDetailViewUrl(finding)} use:link>
          <div class="grid grid-cols-6 hover:bg-gray-50 w-full text-sm px-4 border-b border-gray-100 py-4">
            <div class="flex items-center gap-2 col-span-2">
              <div class="flex gap-2 w-full items-center">
                <div class="flex-shrink-0">
                  <ResourceTypeLogo {finding} showProvider={providers.length > 1} />
                </div>
                <div class="flex flex-col grow gap-1 overflow-hidden">
                  <p class="truncate font-medium text-gray-900">
                    {finding.resources[0].name}
                    {#if finding.resources.length > 1}
                      (And {finding.resources.length} resources more)
                    {/if}
                  </p>
                  <p class="text-xs italic truncate">{finding.securityFindingKind}</p>
                </div>
              </div>
            </div>

            <div class="px-4 py-3 whitespace-nowrap">
              <SeverityBadge {finding} />
            </div>
            <div class="px-4 py-3 whitespace-nowrap">
              <ImpactBadge {finding} />
            </div>
            <div class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
              {((finding.confidence ?? 0) * 100).toFixed(0)}%
            </div>
            <div class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
              <span class="truncate">{toTitleCase(finding.status || "No Status")}</span>
            </div>
          </div>
        </a>
      {/each}
    </div>
  </div>
</div>
